import { useMemo, useState, Fragment } from "react";
import { useResource } from "@/hooks/useResource";
import { LoadingState, EmptyState } from "@/components/common/States";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import api, { apiErrorMessage } from "@/lib/api";
import { toast } from "sonner";
import { Download, Printer, ChevronRight, ChevronDown } from "lucide-react";

export const money = (n) => `₹${Number(n || 0).toLocaleString("en-IN", { maximumFractionDigits: 2 })}`;
export const num = (n) => Number(n || 0).toLocaleString("en-IN", { maximumFractionDigits: 2 });

const ymd = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;

function fyRange() {
  const n = new Date();
  const sy = n.getMonth() >= 3 ? n.getFullYear() : n.getFullYear() - 1;
  return { from: `${sy}-04-01`, to: `${sy + 1}-03-31`, label: `FY ${String(sy).slice(2)}-${String(sy + 1).slice(2)}` };
}

function presetRange(key) {
  const n = new Date();
  const today = ymd(n);
  if (key === "today") return { from: today, to: today };
  if (key === "yesterday") { const y = new Date(n); y.setDate(n.getDate() - 1); return { from: ymd(y), to: ymd(y) }; }
  if (key === "week") { const day = (n.getDay() + 6) % 7; const s = new Date(n); s.setDate(n.getDate() - day); return { from: ymd(s), to: today }; }
  if (key === "month") return { from: `${n.getFullYear()}-${String(n.getMonth() + 1).padStart(2, "0")}-01`, to: today };
  if (key === "lastmonth") { const f = new Date(n.getFullYear(), n.getMonth() - 1, 1); const l = new Date(n.getFullYear(), n.getMonth(), 0); return { from: ymd(f), to: ymd(l) }; }
  if (key === "fy") { const r = fyRange(); return { from: r.from, to: r.to }; }
  return { from: "", to: "" };
}

const PAY_COLORS = {
  PAID: "bg-emerald-100 text-emerald-700", PARTIAL: "bg-amber-100 text-amber-700",
  UNPAID: "bg-rose-100 text-rose-700", CANCELLED: "bg-stone-200 text-stone-600",
};

export function QuickDates({ from, to, setFrom, setTo, onApply }) {
  const fy = fyRange();
  const btns = [["today", "Today"], ["yesterday", "Yesterday"], ["week", "This Week"], ["month", "This Month"], ["lastmonth", "Last Month"], ["fy", fy.label]];
  const pick = (k) => { const r = presetRange(k); setFrom(r.from); setTo(r.to); onApply(r.from, r.to); };
  return (
    <div className="flex flex-wrap items-end gap-2">
      <div className="flex flex-wrap gap-1.5">
        {btns.map(([k, label]) => (
          <Button key={k} data-testid={`qd-${k}`} variant="outline" size="sm" className="h-8 text-xs" onClick={() => pick(k)}>{label}</Button>
        ))}
        <Button data-testid="qd-all" variant="outline" size="sm" className="h-8 text-xs" onClick={() => { setFrom(""); setTo(""); onApply("", ""); }}>All</Button>
      </div>
      <div><Label className="text-[10px]">From</Label><Input type="date" value={from} onChange={(e) => setFrom(e.target.value)} className="h-8 w-36 text-xs" data-testid="rp-from" /></div>
      <div><Label className="text-[10px]">To</Label><Input type="date" value={to} onChange={(e) => setTo(e.target.value)} className="h-8 w-36 text-xs" data-testid="rp-to" /></div>
      <Button size="sm" className="h-8 bg-amber-700 hover:bg-amber-800 text-xs" data-testid="rp-apply" onClick={() => onApply(from, to)}>Apply</Button>
    </div>
  );
}

export function ExportBar({ report, params, testPrefix }) {
  const [busy, setBusy] = useState(false);
  const run = async (fmt) => {
    setBusy(true);
    try {
      const p = new URLSearchParams();
      Object.entries({ ...params, report, format: fmt }).forEach(([k, v]) => { if (v) p.set(k, v); });
      const res = await api.get(`/sales-reports/export-plus?${p.toString()}`, { responseType: "blob" });
      const cd = res.headers["content-disposition"] || "";
      const m = cd.match(/filename="?([^"]+)"?/);
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement("a");
      a.href = url; a.download = m ? m[1] : `${report}.${fmt === "excel" ? "xlsx" : fmt}`;
      document.body.appendChild(a); a.click(); a.remove(); window.URL.revokeObjectURL(url);
      toast.success(`Exported (${fmt.toUpperCase()})`);
    } catch (err) { toast.error(apiErrorMessage(err, "Export failed")); }
    finally { setBusy(false); }
  };
  return (
    <div className="flex gap-1.5">
      <Button data-testid={`${testPrefix}-excel`} variant="outline" size="sm" className="h-8 gap-1 text-xs" disabled={busy} onClick={() => run("excel")}><Download className="h-3.5 w-3.5" /> Excel</Button>
      <Button data-testid={`${testPrefix}-csv`} variant="outline" size="sm" className="h-8 gap-1 text-xs" disabled={busy} onClick={() => run("csv")}><Download className="h-3.5 w-3.5" /> CSV</Button>
      <Button data-testid={`${testPrefix}-pdf`} variant="outline" size="sm" className="h-8 gap-1 text-xs" disabled={busy} onClick={() => run("pdf")}><Download className="h-3.5 w-3.5" /> PDF</Button>
      <Button data-testid={`${testPrefix}-print`} variant="outline" size="sm" className="h-8 gap-1 text-xs" onClick={() => window.print()}><Printer className="h-3.5 w-3.5" /> Print</Button>
    </div>
  );
}

function KpiRow({ items }) {
  return (
    <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-6">
      {items.map(([label, val, tid]) => (
        <div key={label} className="rounded-xl border border-stone-200 bg-white p-3 shadow-sm" data-testid={tid}>
          <div className="font-display text-lg font-bold text-stone-900">{val}</div>
          <div className="mt-0.5 text-[10px] font-semibold uppercase tracking-wide text-stone-500">{label}</div>
        </div>
      ))}
    </div>
  );
}

function DataTable({ cols, rows, empty, testId, onRowClick }) {
  if (!rows || rows.length === 0) return <EmptyState title={empty} message="No data for the selected filters." />;
  return (
    <div className="overflow-x-auto rounded-xl border border-stone-200 bg-white shadow-sm" data-testid={testId}>
      <table className="w-full text-sm">
        <thead className="bg-stone-50 text-left text-xs uppercase tracking-wide text-stone-500">
          <tr>{cols.map((c) => <th key={c.key} className={`px-3 py-2.5 ${c.right ? "text-right" : ""}`}>{c.label}</th>)}</tr>
        </thead>
        <tbody className="divide-y divide-stone-100">
          {rows.map((r, i) => (
            <tr key={i} className={`hover:bg-stone-50/60 ${onRowClick ? "cursor-pointer" : ""}`} onClick={onRowClick ? () => onRowClick(r) : undefined} data-testid={onRowClick ? `${testId}-row-${i}` : undefined}>
              {cols.map((c) => <td key={c.key} className={`px-3 py-2.5 ${c.right ? "text-right tabular-nums" : ""} ${c.mono ? "font-mono text-xs" : "text-stone-700"}`}>{c.fmt ? c.fmt(r[c.key], r) : (r[c.key] ?? "—")}</td>)}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function PayBadge(v) {
  return <Badge className={`${PAY_COLORS[v] || "bg-stone-100 text-stone-600"} border-0 text-[10px]`}>{v || "—"}</Badge>;
}

function useBuyers() {
  const { data } = useResource("/buyers");
  return Array.isArray(data) ? data : data?.items || [];
}

function BuyerSelect({ value, onChange }) {
  const buyers = useBuyers();
  return (
    <select data-testid="rp-buyer" value={value} onChange={(e) => onChange(e.target.value)} className="h-8 w-48 rounded-md border border-stone-200 bg-white px-2 text-xs">
      <option value="">All buyers</option>
      {buyers.map((b) => <option key={b.id || b._id} value={b.buyerId || b.id}>{b.name || b.company}</option>)}
    </select>
  );
}

function SourceSelect({ value, onChange }) {
  return (
    <select data-testid="rp-source" value={value} onChange={(e) => onChange(e.target.value)} className="h-8 w-36 rounded-md border border-stone-200 bg-white px-2 text-xs">
      <option value="">All sources</option>
      <option value="TALLY_IMPORT">Tally Import</option>
      <option value="MANUAL">Manual</option>
    </select>
  );
}

function FilterCard({ children }) {
  return <div className="flex flex-wrap items-end gap-3 rounded-xl border border-stone-200 bg-white p-4 shadow-sm">{children}</div>;
}

/* ============ 1. Consolidated Sales Statement ============ */
export function ConsolidatedStatement() {
  const [from, setFrom] = useState(""); const [to, setTo] = useState("");
  const [buyerId, setBuyerId] = useState(""); const [source, setSource] = useState("");
  const [gstin, setGstin] = useState(""); const [invoiceNumber, setInvoiceNumber] = useState("");
  const [applied, setApplied] = useState({ dateFrom: "", dateTo: "", buyerId: "", source: "", gstin: "", invoiceNumber: "" });
  const apply = (f = from, t = to) => setApplied({ dateFrom: f, dateTo: t, buyerId, source, gstin, invoiceNumber });
  const qs = useMemo(() => { const p = new URLSearchParams(); Object.entries(applied).forEach(([k, v]) => v && p.set(k, v)); const s = p.toString(); return s ? `?${s}` : ""; }, [applied]);
  const { data, isLoading } = useResource(`/sales-reports/consolidated${qs}`);
  const rep = data || {}; const t = rep.totals || {};
  return (
    <div className="space-y-4">
      <FilterCard>
        <QuickDates from={from} to={to} setFrom={setFrom} setTo={setTo} onApply={apply} />
        <div><Label className="text-[10px]">Buyer</Label><BuyerSelect value={buyerId} onChange={setBuyerId} /></div>
        <div><Label className="text-[10px]">Source</Label><SourceSelect value={source} onChange={setSource} /></div>
        <div><Label className="text-[10px]">GSTIN</Label><Input value={gstin} onChange={(e) => setGstin(e.target.value)} placeholder="GSTIN" className="h-8 w-40 text-xs" data-testid="cs-gstin" /></div>
        <div><Label className="text-[10px]">Invoice #</Label><Input value={invoiceNumber} onChange={(e) => setInvoiceNumber(e.target.value)} placeholder="Invoice no" className="h-8 w-36 text-xs" data-testid="cs-invno" /></div>
        <ExportBar report="consolidated" params={applied} testPrefix="cs-export" />
      </FilterCard>
      {isLoading ? <LoadingState label="Loading consolidated statement…" /> : (
        <>
          <KpiRow items={[
            ["Invoices", num(t.invoices), "cs-kpi-invoices"], ["Gross Sales", money(t.gross), "cs-kpi-gross"],
            ["Taxable", money(t.taxable), "cs-kpi-taxable"], ["GST", money(t.gst), "cs-kpi-gst"],
            ["Net Sales", money(t.net), "cs-kpi-net"], ["Outstanding", money(t.outstanding), "cs-kpi-outstanding"],
          ]} />
          <p className="text-xs text-stone-500">CGST {money(t.cgst)} · SGST {money(t.sgst)} · IGST {money(t.igst)} · Credit Notes {money(t.creditNotes)}</p>
          <DataTable testId="cs-table" empty="No confirmed invoices" rows={rep.rows} cols={[
            { key: "invoiceDate", label: "Date" }, { key: "invoiceNumber", label: "Invoice #", mono: true },
            { key: "buyerName", label: "Buyer" }, { key: "parentCompany", label: "Parent Co." },
            { key: "gstin", label: "GSTIN", mono: true },
            { key: "taxable", label: "Taxable", right: true, fmt: money },
            { key: "cgst", label: "CGST", right: true, fmt: money }, { key: "sgst", label: "SGST", right: true, fmt: money },
            { key: "igst", label: "IGST", right: true, fmt: money }, { key: "grand", label: "Total", right: true, fmt: money },
            { key: "outstanding", label: "Outstanding", right: true, fmt: money },
            { key: "paymentStatus", label: "Payment", fmt: PayBadge }, { key: "source", label: "Source" },
          ]} />
        </>
      )}
    </div>
  );
}

/* ============ 2. Item-Wise Sales ============ */
export function ItemWiseReport() {
  const [from, setFrom] = useState(""); const [to, setTo] = useState("");
  const [source, setSource] = useState(""); const [category, setCategory] = useState("");
  const [search, setSearch] = useState("");
  const [applied, setApplied] = useState({ dateFrom: "", dateTo: "", source: "", category: "" });
  const [drill, setDrill] = useState(null);
  const apply = (f = from, t = to) => setApplied({ dateFrom: f, dateTo: t, source, category });
  const qs = useMemo(() => { const p = new URLSearchParams(); Object.entries(applied).forEach(([k, v]) => v && p.set(k, v)); const s = p.toString(); return s ? `?${s}` : ""; }, [applied]);
  const { data, isLoading } = useResource(`/sales-reports/item-wise${qs}`);
  const rep = data || {}; const t = rep.totals || {};
  const rows = useMemo(() => {
    const r = rep.rows || [];
    if (!search.trim()) return r;
    const s = search.toLowerCase();
    return r.filter((x) => [x.productName, x.sku, x.productId].some((v) => String(v || "").toLowerCase().includes(s)));
  }, [rep.rows, search]);
  return (
    <div className="space-y-4">
      <FilterCard>
        <QuickDates from={from} to={to} setFrom={setFrom} setTo={setTo} onApply={apply} />
        <div><Label className="text-[10px]">Source</Label><SourceSelect value={source} onChange={setSource} /></div>
        <div><Label className="text-[10px]">Category</Label>
          <select data-testid="iw-category" value={category} onChange={(e) => setCategory(e.target.value)} className="h-8 w-36 rounded-md border border-stone-200 bg-white px-2 text-xs">
            <option value="">All</option><option value="Bakery">Bakery</option><option value="Non-Bakery">Non-Bakery</option>
          </select></div>
        <div><Label className="text-[10px]">Search product</Label><Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="name / SKU / code" className="h-8 w-44 text-xs" data-testid="iw-search" /></div>
        <ExportBar report="itemwise" params={applied} testPrefix="iw-export" />
      </FilterCard>
      {isLoading ? <LoadingState label="Loading item-wise sales…" /> : (
        <>
          <KpiRow items={[
            ["Products", num(t.products), "iw-kpi-products"], ["Qty Sold", num(t.qty), "iw-kpi-qty"],
            ["Taxable", money(t.taxable), "iw-kpi-taxable"], ["GST", money(t.gst), "iw-kpi-gst"],
            ["Total", money(t.total), "iw-kpi-total"], ["Avg / Unit", t.qty ? money(t.taxable / t.qty) : "—", "iw-kpi-avg"],
          ]} />
          <p className="text-xs text-stone-500">Click a product to drill into buyers &amp; invoices (identify different selling prices). Average Selling Price = Total taxable ÷ Total qty.</p>
          <DataTable testId="iw-table" empty="No products sold" rows={rows} onRowClick={(r) => setDrill(r)} cols={[
            { key: "productId", label: "Product ID", mono: true }, { key: "productName", label: "Product" },
            { key: "sku", label: "SKU", mono: true }, { key: "unit", label: "Unit" },
            { key: "qty", label: "Qty", right: true, fmt: num }, { key: "invoices", label: "Bills", right: true },
            { key: "buyers", label: "Buyers", right: true },
            { key: "taxable", label: "Taxable", right: true, fmt: money }, { key: "gst", label: "GST", right: true, fmt: money },
            { key: "total", label: "Total", right: true, fmt: money },
            { key: "avgPrice", label: "Avg Sell Price", right: true, fmt: money },
          ]} />
        </>
      )}
      <ItemDrillDown item={drill} dateFrom={applied.dateFrom} dateTo={applied.dateTo} source={applied.source} onClose={() => setDrill(null)} />
    </div>
  );
}

function ItemDrillDown({ item, dateFrom, dateTo, source, onClose }) {
  const qs = useMemo(() => { if (!item) return ""; const p = new URLSearchParams(); if (dateFrom) p.set("dateFrom", dateFrom); if (dateTo) p.set("dateTo", dateTo); if (source) p.set("source", source); const s = p.toString(); return s ? `?${s}` : ""; }, [item, dateFrom, dateTo, source]);
  const { data, isLoading } = useResource(item ? `/sales-reports/item-wise/${item.productId}/detail${qs}` : null, { enabled: !!item });
  const byBuyer = useMemo(() => {
    const rows = data?.rows || [];
    const m = {};
    rows.forEach((r) => { (m[r.buyerName] = m[r.buyerName] || { buyerName: r.buyerName, parentCompany: r.parentCompany, invoices: [], qty: 0, value: 0 }); const g = m[r.buyerName]; g.invoices.push(r); g.qty += r.qty; g.value += r.taxable; });
    return Object.values(m).sort((a, b) => b.value - a.value);
  }, [data]);
  return (
    <Dialog open={!!item} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-3xl max-h-[85vh] overflow-y-auto" data-testid="iw-drill">
        <DialogHeader><DialogTitle>{item?.productName} <span className="font-mono text-xs text-stone-400">{item?.productId}</span></DialogTitle></DialogHeader>
        {isLoading ? <LoadingState label="Loading buyers…" /> : byBuyer.length === 0 ? <EmptyState title="No sales" message="No confirmed sales for this product in range." /> : (
          <div className="space-y-4">
            {byBuyer.map((g, gi) => (
              <div key={gi} className="rounded-lg border border-stone-200" data-testid={`iw-drill-buyer-${gi}`}>
                <div className="flex items-center justify-between bg-stone-50 px-3 py-2">
                  <div><div className="font-semibold text-stone-800">{g.buyerName}</div><div className="text-[10px] text-stone-500">{g.parentCompany}</div></div>
                  <div className="text-right text-xs text-stone-600">Qty {num(g.qty)} · {money(g.value)}</div>
                </div>
                <table className="w-full text-xs">
                  <thead className="text-left text-[10px] uppercase text-stone-400"><tr><th className="px-3 py-1.5">Invoice</th><th className="px-3 py-1.5">Date</th><th className="px-3 py-1.5 text-right">Qty</th><th className="px-3 py-1.5 text-right">Rate</th><th className="px-3 py-1.5 text-right">Value</th></tr></thead>
                  <tbody className="divide-y divide-stone-100">
                    {g.invoices.map((r, ri) => (
                      <tr key={ri}><td className="px-3 py-1.5 font-mono">{r.invoiceNumber}</td><td className="px-3 py-1.5">{r.invoiceDate}</td><td className="px-3 py-1.5 text-right tabular-nums">{num(r.qty)}</td><td className="px-3 py-1.5 text-right tabular-nums">{money(r.rate)}</td><td className="px-3 py-1.5 text-right tabular-nums">{money(r.taxable)}</td></tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ))}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

/* ============ 3. Buyer / Parent Company ============ */
export function ParentCompanyReport() {
  const [from, setFrom] = useState(""); const [to, setTo] = useState(""); const [source, setSource] = useState("");
  const [applied, setApplied] = useState({ dateFrom: "", dateTo: "", source: "" });
  const [open, setOpen] = useState({});
  const apply = (f = from, t = to) => setApplied({ dateFrom: f, dateTo: t, source });
  const qs = useMemo(() => { const p = new URLSearchParams(); Object.entries(applied).forEach(([k, v]) => v && p.set(k, v)); const s = p.toString(); return s ? `?${s}` : ""; }, [applied]);
  const { data, isLoading } = useResource(`/sales-reports/parent-company${qs}`);
  const rep = data || {}; const t = rep.totals || {};
  return (
    <div className="space-y-4">
      <FilterCard>
        <QuickDates from={from} to={to} setFrom={setFrom} setTo={setTo} onApply={apply} />
        <div><Label className="text-[10px]">Source</Label><SourceSelect value={source} onChange={setSource} /></div>
        <ExportBar report="parentcompany" params={applied} testPrefix="pc-export" />
      </FilterCard>
      {isLoading ? <LoadingState label="Loading parent-company sales…" /> : (
        <>
          <KpiRow items={[
            ["Parent Cos.", num(t.parents), "pc-kpi-parents"], ["Invoices", num(t.invoices), "pc-kpi-invoices"],
            ["Qty", num(t.qty), "pc-kpi-qty"], ["Taxable", money(t.taxable), "pc-kpi-taxable"],
            ["Revenue", money(t.revenue), "pc-kpi-revenue"], ["Outstanding", money(t.outstanding), "pc-kpi-outstanding"],
          ]} />
          {(rep.rows || []).length === 0 ? <EmptyState title="No sales" message="No confirmed sales for the selected filters." /> : (
            <div className="overflow-x-auto rounded-xl border border-stone-200 bg-white shadow-sm" data-testid="pc-table">
              <table className="w-full text-sm">
                <thead className="bg-stone-50 text-left text-xs uppercase tracking-wide text-stone-500">
                  <tr><th className="px-3 py-2.5">Parent Company / Store</th><th className="px-3 py-2.5 text-right">Invoices</th><th className="px-3 py-2.5 text-right">Qty</th><th className="px-3 py-2.5 text-right">Taxable</th><th className="px-3 py-2.5 text-right">GST</th><th className="px-3 py-2.5 text-right">Revenue</th><th className="px-3 py-2.5 text-right">Outstanding</th></tr>
                </thead>
                <tbody className="divide-y divide-stone-100">
                  {rep.rows.map((p, pi) => (
                    <Fragment key={`p${pi}`}>
                      <tr className="cursor-pointer bg-amber-50/40 font-semibold hover:bg-amber-50" onClick={() => setOpen((o) => ({ ...o, [p.parentCompanyId]: !o[p.parentCompanyId] }))} data-testid={`pc-parent-${pi}`}>
                        <td className="px-3 py-2.5 text-stone-800">{open[p.parentCompanyId] ? <ChevronDown className="mr-1 inline h-3.5 w-3.5" /> : <ChevronRight className="mr-1 inline h-3.5 w-3.5" />}{p.parentCompany} <span className="text-[10px] font-normal text-stone-400">({p.stores.length} store{p.stores.length !== 1 ? "s" : ""})</span></td>
                        <td className="px-3 py-2.5 text-right tabular-nums">{p.invoices}</td><td className="px-3 py-2.5 text-right tabular-nums">{num(p.qty)}</td>
                        <td className="px-3 py-2.5 text-right tabular-nums">{money(p.taxable)}</td><td className="px-3 py-2.5 text-right tabular-nums">{money(p.gst)}</td>
                        <td className="px-3 py-2.5 text-right tabular-nums">{money(p.revenue)}</td><td className="px-3 py-2.5 text-right tabular-nums">{money(p.outstanding)}</td>
                      </tr>
                      {open[p.parentCompanyId] && p.stores.map((s, si) => (
                        <tr key={`p${pi}s${si}`} className="bg-white text-stone-600" data-testid={`pc-store-${pi}-${si}`}>
                          <td className="px-3 py-2 pl-9">{s.store} <span className="font-mono text-[10px] text-stone-400">{s.gstin}</span></td>
                          <td className="px-3 py-2 text-right tabular-nums">{s.invoices}</td><td className="px-3 py-2 text-right tabular-nums">{num(s.qty)}</td>
                          <td className="px-3 py-2 text-right tabular-nums">{money(s.taxable)}</td><td className="px-3 py-2 text-right tabular-nums">{money(s.gst)}</td>
                          <td className="px-3 py-2 text-right tabular-nums">{money(s.revenue)}</td><td className="px-3 py-2 text-right tabular-nums">{money(s.outstanding)}</td>
                        </tr>
                      ))}
                    </Fragment>
                  ))}
                </tbody>
              </table>
            </div>
          )}
          <p className="text-xs text-stone-500">Branches are kept distinct even when they share a GSTIN — the store (buyer) record remains the transaction identity.</p>
        </>
      )}
    </div>
  );
}

/* ============ 5. Date-Wise Sales ============ */
export function DateWiseReport() {
  const [from, setFrom] = useState(""); const [to, setTo] = useState("");
  const [buyerId, setBuyerId] = useState(""); const [source, setSource] = useState("");
  const [applied, setApplied] = useState({ dateFrom: "", dateTo: "", buyerId: "", source: "" });
  const apply = (f = from, t = to) => setApplied({ dateFrom: f, dateTo: t, buyerId, source });
  const qs = useMemo(() => { const p = new URLSearchParams(); Object.entries(applied).forEach(([k, v]) => v && p.set(k, v)); const s = p.toString(); return s ? `?${s}` : ""; }, [applied]);
  const { data, isLoading } = useResource(`/sales-reports/date-wise${qs}`);
  const rep = data || {}; const t = rep.totals || {};
  return (
    <div className="space-y-4">
      <FilterCard>
        <QuickDates from={from} to={to} setFrom={setFrom} setTo={setTo} onApply={apply} />
        <div><Label className="text-[10px]">Buyer</Label><BuyerSelect value={buyerId} onChange={setBuyerId} /></div>
        <div><Label className="text-[10px]">Source</Label><SourceSelect value={source} onChange={setSource} /></div>
        {/* date-wise export reuses the ledger-style export via a dedicated report key */}
        <ExportBar report="datewise" params={applied} testPrefix="dw-export" />
      </FilterCard>
      {isLoading ? <LoadingState label="Loading date-wise sales…" /> : (
        <>
          <KpiRow items={[
            ["Days", num(t.days), "dw-kpi-days"], ["Invoices", num(t.invoices), "dw-kpi-invoices"],
            ["Qty", num(t.qty), "dw-kpi-qty"], ["Taxable", money(t.taxable), "dw-kpi-taxable"],
            ["GST", money(t.gst), "dw-kpi-gst"], ["Total", money(t.total), "dw-kpi-total"],
          ]} />
          <DataTable testId="dw-table" empty="No sales" rows={rep.rows} cols={[
            { key: "date", label: "Date" }, { key: "invoices", label: "Invoices", right: true },
            { key: "qty", label: "Qty", right: true, fmt: num }, { key: "taxable", label: "Taxable", right: true, fmt: money },
            { key: "gst", label: "GST", right: true, fmt: money }, { key: "total", label: "Total", right: true, fmt: money },
          ]} />
        </>
      )}
    </div>
  );
}

export function SalesLedgerReport() {
  const [from, setFrom] = useState(""); const [to, setTo] = useState("");
  const [buyerId, setBuyerId] = useState(""); const [source, setSource] = useState("");
  const [search, setSearch] = useState(""); const [sortBy, setSortBy] = useState("date");
  const [sortDir, setSortDir] = useState("desc"); const [page, setPage] = useState(1);
  const [applied, setApplied] = useState({ dateFrom: "", dateTo: "", buyerId: "", source: "", search: "", sortBy: "date", sortDir: "desc" });
  const [showMissing, setShowMissing] = useState(true);
  const pageSize = 100;
  const apply = (f = from, t = to) => { setPage(1); setApplied({ dateFrom: f, dateTo: t, buyerId, source, search, sortBy, sortDir }); };
  const qs = useMemo(() => { const p = new URLSearchParams(); Object.entries({ ...applied, page, pageSize }).forEach(([k, v]) => v && p.set(k, v)); return `?${p.toString()}`; }, [applied, page]);
  const { data, isLoading } = useResource(`/sales-reports/ledger${qs}`);
  const rep = data || {}; const t = rep.totals || {};
  const total = rep.total || 0; const pages = Math.max(1, Math.ceil(total / pageSize));
  return (
    <div className="space-y-4">
      <FilterCard>
        <QuickDates from={from} to={to} setFrom={setFrom} setTo={setTo} onApply={apply} />
        <div><Label className="text-[10px]">Buyer</Label><BuyerSelect value={buyerId} onChange={setBuyerId} /></div>
        <div><Label className="text-[10px]">Source</Label><SourceSelect value={source} onChange={setSource} /></div>
        <div><Label className="text-[10px]">Search</Label><Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="invoice / buyer / GSTIN" className="h-8 w-48 text-xs" data-testid="lg-search" /></div>
        <div><Label className="text-[10px]">Sort</Label>
          <select data-testid="lg-sortby" value={sortBy} onChange={(e) => setSortBy(e.target.value)} className="h-8 w-28 rounded-md border border-stone-200 bg-white px-2 text-xs">
            <option value="date">Date</option><option value="invoice">Invoice #</option><option value="buyer">Buyer</option><option value="amount">Amount</option>
          </select></div>
        <div><Label className="text-[10px]">Order</Label>
          <select data-testid="lg-sortdir" value={sortDir} onChange={(e) => setSortDir(e.target.value)} className="h-8 w-24 rounded-md border border-stone-200 bg-white px-2 text-xs">
            <option value="desc">Desc</option><option value="asc">Asc</option>
          </select></div>
        <ExportBar report="ledger" params={applied} testPrefix="lg-export" />
      </FilterCard>
      {isLoading ? <LoadingState label="Loading sales ledger…" /> : (
        <>
          <KpiRow items={[
            ["Invoices", num(t.invoices), "lg-kpi-invoices"], ["Taxable", money(t.taxable), "lg-kpi-taxable"],
            ["GST", money(t.gst), "lg-kpi-gst"], ["Total", money(t.grand), "lg-kpi-grand"],
            ["Outstanding", money(t.outstanding), "lg-kpi-outstanding"], ["Missing #", num(t.missing), "lg-kpi-missing"],
          ]} />

          {(rep.missing || []).length > 0 && (
            <div className="rounded-xl border border-rose-200 bg-rose-50/50 p-4 shadow-sm" data-testid="lg-missing-panel">
              <button className="flex w-full items-center justify-between text-left" onClick={() => setShowMissing((s) => !s)} data-testid="lg-missing-toggle">
                <span className="font-semibold text-rose-800">⚠ Possible Missing Invoice Numbers ({rep.missing.length})</span>
                {showMissing ? <ChevronDown className="h-4 w-4 text-rose-700" /> : <ChevronRight className="h-4 w-4 text-rose-700" />}
              </button>
              {showMissing && (
                <div className="mt-3 overflow-x-auto">
                  <table className="w-full text-xs">
                    <thead className="text-left text-[10px] uppercase text-rose-500"><tr><th className="px-3 py-1.5">Series</th><th className="px-3 py-1.5">FY</th><th className="px-3 py-1.5">Missing #</th><th className="px-3 py-1.5">Previous</th><th className="px-3 py-1.5">Next</th><th className="px-3 py-1.5">Source</th></tr></thead>
                    <tbody className="divide-y divide-rose-100">
                      {rep.missing.map((m, mi) => (
                        <tr key={mi} data-testid={`lg-missing-${mi}`}>
                          <td className="px-3 py-1.5 font-mono text-stone-600">{m.series}</td><td className="px-3 py-1.5">{m.financialYear || "—"}</td>
                          <td className="px-3 py-1.5 font-mono font-semibold text-rose-700">{m.missingNumber}</td>
                          <td className="px-3 py-1.5 font-mono text-stone-500">{m.previousInvoice || "—"}</td>
                          <td className="px-3 py-1.5 font-mono text-stone-500">{m.nextInvoice || "—"}</td>
                          <td className="px-3 py-1.5">{m.source}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                  <p className="mt-2 text-[10px] text-rose-600">Cancelled invoices are not flagged as missing. Tally and internal (SB) series are analysed separately.</p>
                </div>
              )}
            </div>
          )}

          <DataTable testId="lg-table" empty="No invoices" rows={rep.rows} cols={[
            { key: "invoiceDate", label: "Date" }, { key: "invoiceNumber", label: "Invoice #", mono: true },
            { key: "buyerName", label: "Buyer" }, { key: "parentCompany", label: "Parent Co." }, { key: "store", label: "Store" },
            { key: "gstin", label: "GSTIN", mono: true }, { key: "taxable", label: "Taxable", right: true, fmt: money },
            { key: "gst", label: "GST", right: true, fmt: money }, { key: "grand", label: "Total", right: true, fmt: money },
            { key: "outstanding", label: "Outstanding", right: true, fmt: money },
            { key: "paymentStatus", label: "Payment", fmt: PayBadge }, { key: "source", label: "Source" }, { key: "status", label: "Status" },
          ]} />

          <div className="flex items-center justify-between text-xs text-stone-500">
            <span>Showing {total === 0 ? 0 : (page - 1) * pageSize + 1}–{Math.min(page * pageSize, total)} of {total}</span>
            <div className="flex gap-2">
              <Button variant="outline" size="sm" className="h-7 text-xs" disabled={page <= 1} onClick={() => setPage((p) => p - 1)} data-testid="lg-prev">Prev</Button>
              <span className="py-1">Page {page} / {pages}</span>
              <Button variant="outline" size="sm" className="h-7 text-xs" disabled={page >= pages} onClick={() => setPage((p) => p + 1)} data-testid="lg-next">Next</Button>
            </div>
          </div>
        </>
      )}
    </div>
  );
}
